import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import dayjs from "utils/dayjs";
import { GET_TRANSACTION_AMOUNTS } from "graphql/transaction";
import TransactionStatus from "enums/TransactionStatus";
import { TimeFrameEnum } from "enums/AnalyticsTimeFrame";
import {
  DISPUTE_MONEY_TYPES,
  SPEND_TYPES,
} from "utils/transactions/transactionGroups";

export interface WeeklySpendComparison {
  /** How many calendar-week buckets the two months need between them. */
  weekCount: number;
  /** Spend per bucket for the current month, positive numbers. */
  thisMonth: number[];
  /** Spend per bucket for the previous month, positive numbers. */
  lastMonth: number[];
  /** The ledger's currency, so the panel does not format a tenant in dollars. */
  currency: string;
  loading: boolean;
}

/**
 * Calendar weeks, not ISO weeks: the buckets have to line up across two months
 * of different lengths, and "the first seven days" does that where a week
 * number does not.
 */
const WEEK_LENGTH = 7;
const MAX_WEEKS = 5;

const SPEND_STATUSES = [
  TransactionStatus.COMPLETE,
  TransactionStatus.PENDING,
  TransactionStatus.REFUNDED,
  TransactionStatus.PARTIALLY_REFUNDED,
];

interface SpendQuery {
  transactions?: {
    edges?:
      | {
          node: {
            id: string;
            amount?: number | null;
            currency?: string | null;
            transactionDate?: number | null;
            type?: string | null;
          };
        }[]
      | null;
  } | null;
}

const bucketOf = (date: number, weeks: number): number =>
  Math.min(Math.floor((dayjs(date).date() - 1) / WEEK_LENGTH), weeks - 1);

/** The currency the rows are denominated in, from the first row that names one. */
const currencyOf = (...data: (SpendQuery | undefined)[]): string | null => {
  for (const set of data) {
    const found = (set?.transactions?.edges ?? []).find(
      ({ node }) => !!node.currency,
    );
    if (found) return found.node.currency ?? null;
  }
  return null;
};

const nets = (type?: string | null): boolean =>
  DISPUTE_MONEY_TYPES.some((disputeType) => disputeType === type);

/** Spend per calendar week, so a short month's trailing bucket stays empty. */
const bucketSpend = (data?: SpendQuery, weeks = MAX_WEEKS): number[] => {
  const buckets = new Array<number>(weeks).fill(0);

  (data?.transactions?.edges ?? []).forEach(({ node }) => {
    const amount = node.amount ?? 0;
    if (!node.transactionDate || (amount >= 0 && !nets(node.type))) return;
    buckets[bucketOf(node.transactionDate, weeks)] -= amount;
  });

  return buckets.map((spend) => Math.max(spend, 0));
};

/**
 * This month against last month, in calendar-week buckets, for the spend
 * comparison panel. The ledger query has no per-week aggregate, so the split
 * happens here off each month's rows.
 */
const useWeeklySpendComparison = (): WeeklySpendComparison => {
  const variables = {
    type: SPEND_TYPES,
    transactionStatus: SPEND_STATUSES,
  };

  const { data: current, loading: currentLoading } = useQuery<SpendQuery>(
    GET_TRANSACTION_AMOUNTS,
    {
      variables: { ...variables, date: TimeFrameEnum.THIS_MONTH },
      fetchPolicy: "cache-and-network",
    },
  );

  const { data: previous, loading: previousLoading } = useQuery<SpendQuery>(
    GET_TRANSACTION_AMOUNTS,
    {
      variables: { ...variables, date: TimeFrameEnum.LAST_MONTH },
      fetchPolicy: "cache-and-network",
    },
  );

  return useMemo(() => {
    const spanOf = (month: dayjs.Dayjs) =>
      Math.ceil(month.daysInMonth() / WEEK_LENGTH);
    const weekCount = Math.max(
      spanOf(dayjs()),
      spanOf(dayjs().subtract(1, "month")),
    );

    return {
      weekCount,
      thisMonth: bucketSpend(current, weekCount),
      lastMonth: bucketSpend(previous, weekCount),
      currency: currencyOf(current, previous) ?? "usd",
      loading: currentLoading || previousLoading,
    };
  }, [current, previous, currentLoading, previousLoading]);
};

export default useWeeklySpendComparison;
